import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";

/**
 * Menu ancore rapide per saltare alle sezioni della home senza scorrere.
 * Se l'utente è su una pagina diversa dalla home, il click naviga a "/" e
 * poi scrolla all'ancora.
 */
const SECTIONS = [
  { id: "sec-direttore", label: "Contatto Direttore" },
  { id: "sec-sponsor", label: "Sponsor" },
  { id: "sec-missione", label: "Le nostre missioni" },
  { id: "sec-categorie", label: "Categorie eventi" },
  { id: "sec-servizi", label: "Cosa fa NMA" },
  { id: "sec-community", label: "Community · Social" },
  { id: "sec-partner", label: "Media Partner" },
  { id: "sec-candidati", label: "Candidature artisti" },
];

export default function SectionsMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // On landing route, if there's a hash, scroll to it (handles /#sec-x links from other pages)
  useEffect(() => {
    if (location.pathname === "/" && location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    }
  }, [location.pathname, location.hash]);

  const goToSection = (sid) => {
    setOpen(false);
    if (location.pathname === "/") {
      const el = document.getElementById(sid);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      // keep url tidy with hash
      window.history.replaceState(null, "", `/#${sid}`);
    } else {
      navigate(`/#${sid}`);
    }
  };

  return (
    <div className="relative" ref={ref} data-testid="sections-menu">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="hidden lg:inline-flex items-center gap-1 text-sm text-[#F7F4EE]/90 hover:text-[#E5A93C] transition-colors px-2 py-2 whitespace-nowrap border av-hairline rounded-full bg-[#141210]/60 hover:border-[#E5A93C]/60"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="sections-menu-trigger"
      >
        Sezioni <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 mt-2 min-w-[220px] rounded-xl border av-hairline bg-[#0a0908]/95 backdrop-blur-md shadow-2xl py-2 z-[60]"
          data-testid="sections-menu-list"
        >
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              role="menuitem"
              onClick={() => goToSection(s.id)}
              className="w-full text-left px-4 py-2 text-sm text-[#F7F4EE]/85 hover:bg-[#E5A93C]/10 hover:text-[#E5A93C] transition-colors"
              data-testid={`sections-menu-item-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
